import { TBTFormWidget, TBTFormWidgetFields } from '@melio/form-controls';
import { Button, Group, Link, Text, useFormSubmissionController } from '@melio/penny';
import { CardHolderDetails } from '@melio/platform-api';
import { forwardRef, useBoolean } from '@melio/platform-utils';
import { useEffect, useState } from 'react';

import { GuestPaymentLayout, GuestPaymentLayoutProps } from '../../../layout';
import { CardHolderDetailsForm } from '../components';

export type CardHolderDetailsFormFields = TBTFormWidgetFields & CardHolderDetails;

export type AddCardScreenProps = {
  onBack?: VoidFunction;
  billId?: GuestPaymentLayoutProps['billId'];
  onSubmit: (data: CardHolderDetailsFormFields) => unknown;
  isSaving?: boolean;
};

export const AddCardScreen = forwardRef<AddCardScreenProps>(({ onSubmit, isSaving, ...props }, ref) => {
  const { onSubmissionStateChange: onCardDetailsFormStateChange, submitButtonProps: cardDetailsSubmitButtonProps } =
    useFormSubmissionController<TBTFormWidgetFields>();

  const {
    onSubmissionStateChange: onCardHolderDetailsFormStateChange,
    submitButtonProps: cardHolderDetailsSubmitButtonProps,
  } = useFormSubmissionController<CardHolderDetails>();

  const onClick = () => {
    cardDetailsSubmitButtonProps?.onClick();
    cardHolderDetailsSubmitButtonProps?.onClick();
  };

  const [isReady, ready] = useBoolean(false);

  const [cardDetails, setCardDetails] = useState<TBTFormWidgetFields>();
  const [cardHolderDetails, setCardHolderDetails] = useState<CardHolderDetails>();

  useEffect(() => {
    if (cardDetails && cardHolderDetails) {
      onSubmit({ ...cardDetails, ...cardHolderDetails });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardDetails, cardHolderDetails]);

  return (
    <GuestPaymentLayout data-component={AddCardScreen.displayName} title="Enter your card details" {...props} ref={ref}>
      <Group variant="vertical" spacing="l">
        <TBTFormWidget
          onSubmissionStateChange={onCardDetailsFormStateChange}
          onSubmit={setCardDetails}
          isSaving={isSaving}
          onReady={ready.on}
          size="small"
          hideCardLogos
        />
        {isReady && (
          <>
            <Group variant="vertical" spacing="s">
              <Text color="neutral.darker" textStyle="body4SemiUpper">
                Card holder details
              </Text>
              <CardHolderDetailsForm
                onSubmit={setCardHolderDetails}
                onSubmissionStateChange={onCardHolderDetailsFormStateChange}
                isSaving={isSaving}
                size="small"
              />
            </Group>
            <Group variant="vertical" spacing="s">
              <Text color="neutral.darker" textStyle="body4">
                By continuing you agree to Melio’s <CustomLink label="Terms of Service" /> and{' '}
                <CustomLink label="Privacy Policy" />
              </Text>
              <Button
                isDisabled={cardDetailsSubmitButtonProps?.isDisabled || cardHolderDetailsSubmitButtonProps?.isDisabled}
                isLoading={cardDetailsSubmitButtonProps?.isLoading || cardHolderDetailsSubmitButtonProps?.isLoading}
                onClick={onClick}
                variant="primary"
                data-testid="add-card-screen-submit-button"
                label="Submit and continue"
              />
            </Group>
          </>
        )}
      </Group>
    </GuestPaymentLayout>
  );
});
AddCardScreen.displayName = 'AddCardScreen';

const CustomLink = (props: { label: string; href?: string }) => (
  <Link onClick={(e) => e.preventDefault()} variant="inline" color="inherit" href="#" {...props} />
);
